import { zoomIdentity } from "d3";
import { BehaviorSubject, combineLatest } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import clamp from "../../../../common/helpers/clamp";
import LinearScale from "./LinearScale";
import interpolateTransition from "../../helpers/interpolateTransition";
import bSub from "../../helpers/convertToBSubject";
import calcTicks from "./calcTicks";
import parsePositionStr from "./parsePositionStr";

class ZoomService {
  constructor() {
    this.zoomId = zoomIdentity;
    this.maxBasePXsize = 160;
    this.xPadding = 65;
    this.minBases = 0;
    this.maxZoomFactor = Infinity;
    this.mousePosX$ = new BehaviorSubject(-10000);
    this.mousePosY$ = new BehaviorSubject(-10000);
    this.mousePos$ = new BehaviorSubject({});

    this.isWholeGenome$ = new BehaviorSubject(false);
    this.isInverted$ = new BehaviorSubject(false);
    this.yUpdate$ = new BehaviorSubject({});
    this.zoomId$ = new BehaviorSubject(this.zoomId);
    this.processes$ = new BehaviorSubject(0);
    this.position$ = new BehaviorSubject({});
    this.xVisScale$ = new BehaviorSubject(new LinearScale());
    this.xScale$ = new BehaviorSubject(new LinearScale());
    this.yScale$ = new BehaviorSubject(new LinearScale());
    this.genome = { g0: 0, g1: 1, chrs: [] };

    this.scroll$ = bSub(
      this.zoomId$.pipe(
        map((x) => x.y),
        distinctUntilChanged((a, b) => Math.abs(a - b) < 0.5)
      ),
      0
    );

    this.zoomSub = combineLatest([this.xVisScale$, this.xScale$]).subscribe(
      () => {
        const { r1, d1, domainDist } = this.xScale$.value;
        const xVisDist = this.xVisScale$.value.domainDist;

        const k = domainDist / xVisDist || 1;
        const x = this.xVisScale$.value.scale(d1);

        this.zoomId.x = x - r1 * k;
        this.zoomId.k = k;
      }
    );
  }

  calcMaxZoomFactor(xScale) {
    const { rangeDist, domainDist } = xScale;
    const minBases = rangeDist / this.maxBasePXsize;
    this.maxZoomFactor = domainDist / minBases - 1;
    this.minBases = minBases;
  }

  dispose() {
    clearInterval(this.s);
    this.mousePosX$.complete();
    this.isWholeGenome$.complete();
    this.isInverted$.complete();
    this.zoomId$.complete();
    this.processes$.complete();
    this.position$.complete();
    this.zoomSub.unsubscribe();
  }

  parsePositionStr = (posStr) => {
    return parsePositionStr(posStr, this.genome);
  };

  setIsWholeGenome(isWgVal) {
    const { value: xVisScale } = this.xVisScale$;
    const { d1, d2 } = xVisScale;
    this.isWholeGenome$.next(isWgVal);

    if (isWgVal) {
      const { g0, g1 } = this.genome;
      this.setXDomain(g0, g1);
    } else {
      const chr = this.genome.findChr((d1 + d2) / 2);
      const { g0, g1 } = chr.region;
      this.setXDomain(g0, g1);
      this.setXVisDomain(clamp(d1, g0, g1), clamp(d2, g0, g1));
    }
  }

  recalcPositionInfo = () => {
    const { value: isWs } = this.isWholeGenome$;
    const { value: xVis } = this.xVisScale$;
    const { start: s, end: e } = xVis;
    const { chrs } = this.genome;
    const segs = chrs.map((x) => x.calcOverlaped(s, e)).filter(Boolean);
    const ticks = calcTicks(chrs, segs, xVis, isWs);
    const pos = { chrSegments: segs, scaleTicks: ticks, genome: this.genome };

    this.position$.next(pos);
    return pos;
  };

  toggleInversion = () => {
    const { d1, d2 } = this.xScale$.value;
    const { d1: dv1, d2: dv2 } = this.xVisScale$.value;
    const { value: isInverted } = this.isInverted$;

    this.isInverted$.next(!isInverted);
    this.setXDomain(d1, d2);
    this.setXVisDomain(dv1, dv2);
  };

  setXDomain = (x1, x2) => {
    const { value: isInverted } = this.isInverted$;
    const dMin = x1 < x2 ? x1 : x2;
    const dMax = x1 < x2 ? x2 : x1;
    const d1 = isInverted ? dMax : dMin;
    const d2 = isInverted ? dMin : dMax;
    this.updateXScale({ d1, d2 });
  };

  setXVisDomain = (x1, x2) => {
    const { value: isInverted } = this.isInverted$;
    const dMin = x1 < x2 ? x1 : x2;
    const dMax = x1 < x2 ? x2 : x1;
    const d1 = isInverted ? dMax : dMin;
    const d2 = isInverted ? dMin : dMax;
    this.updateXVisScale({ d1, d2 });
  };

  setConf = ({ genome, chromosome, visRange }) => {
    const isWg = this.isWholeGenome$.value;
    const x0 = isWg ? genome?.g0 : chromosome?.region?.g0;
    const x1 = isWg ? genome?.g1 : chromosome?.region?.g1;
    const isNotInitialized = this.xVisScale$.value.d1 === -1;

    this.genome = genome;
    this.setXDomain(x0, x1);

    if (isNotInitialized) this.setXVisDomain(visRange.g0, visRange.g1);
    else this.jumpTo(visRange.g0, visRange.g1);
  };

  setZoomId(transform) {
    const { scale } = this.xScale$.value;
    const [d1, d2] = transform.rescaleX(scale).domain();
    this.zoomId = transform;
    this.updateXVisScale({ d1, d2 });
    this.zoomId$.next(this.zoomId);
  }

  jumpTo = (x0, x1) => {
    const { d1, d2 } = this.xVisScale$.value;
    const { d1: xd1Orig, d2: xd2Orig } = this.xScale$.value;

    if (!(x0 >= xd1Orig && x1 <= xd2Orig)) {
      this.setIsWholeGenome(true);
    }

    const { d1: xd1, d2: xd2 } = this.xScale$.value;
    const isInverted = d2 - d1 < 0;
    const addPadding = this.minBases - (x1 - x0);
    const pad = addPadding > 0 ? addPadding / 2 : 0;
    const p1 = x0 - pad;
    const p2 = x1 + pad;
    const [start, end] = isInverted ? [p2, p1] : [p1, p2];
    const min = Math.min(xd1, xd2);
    const max = Math.max(xd1, xd2);
    const toD1 = clamp(start, min, max);
    const toD2 = clamp(end, min, max);

    interpolateTransition(this.setXVisDomain, 200, d1, d2, toD1, toD2);
  };

  buttonZoom = (isZoomIn) => {
    const { domainDist, d1, d2 } = this.xVisScale$.value;
    const min = Math.min(d1, d2);
    const max = Math.max(d1, d2);
    const diffMultiplier = isZoomIn ? -1 : 1;
    const dist = isZoomIn ? domainDist / 4 : domainDist / 2;
    const d = dist * diffMultiplier;
    this.jumpTo(min - d, max + d);
  };

  buttonPan = (isLeft) => {
    const { domainDist, d1, d2 } = this.xVisScale$.value;
    const isInversedMult = d2 - d1 < 0 ? -1 : 1;
    const mult = isLeft ? -0.5 : 0.5;
    const d = Math.abs(domainDist) * mult * isInversedMult;
    const min = Math.min(d1, d2);
    const max = Math.max(d1, d2);
    const x0 = min + d;
    const x1 = max + d;
    this.jumpTo(x0, x1);
  };

  getPositionStr = () => {
    const { chrSegments } = this.position$.value || {};
    const segs = chrSegments || [];
    if (segs.length === 0) return "";
    const first = segs[0];
    const last = segs[segs.length - 1];
    const firstChr = first?.chr?.key;
    const lastChr = last?.chr?.key;
    const isSameChr = firstChr === lastChr;
    const secondChrSignifier = isSameChr ? "" : `${lastChr}:`;
    const x0 = Math.floor(Math.max(1, first?.x0)).toLocaleString("en-US");
    const x1 = Math.ceil(last?.x1).toLocaleString("en-US");
    return `${firstChr}:${x0}..${secondChrSignifier}${x1}`;
  };

  updateXVisScale = (conf) => {
    this.xVisScale$.next(this.xVisScale$.value.setValues(conf));
  };

  updateXScale = (conf) => {
    this.xScale$.next(this.xScale$.value.setValues(conf));
    this.calcMaxZoomFactor(this.xScale$.value);
  };

  setYDomain = (d1, d2) => {
    this.yScale$.next(this.yScale$.value.setValues({ d1, d2 }));
  };

  setYRange = (r1, r2) => {
    this.yScale$.next(this.yScale$.value.setValues({ r1, r2 }));
  };

  setXVisRange = (r1, r2) => this.updateXVisScale({ r1, r2 });

  setXRange = (r1, r2) => this.updateXScale({ r1, r2 });

  updateInversion = (val) => this.isInverted$.next(val);

  setMousePosX = (x) => {
    this.mousePosX$.next(x);
    this.mousePos$.next({ x, y: this.mousePosY$.value });
  };

  setMousePosY = (y) => {
    this.mousePosY$.next(y);
    this.mousePos$.next({ x: this.mousePosX$.value, y });
  };

  onBlockStarted = () => this.processes$.next(this.processes$.value + 1);

  onBlockEnded = () => this.processes$.next(this.processes$.value - 1);
}

export default ZoomService;
